import { Suspense } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faBuilding, faFileExport, faFileImport } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecurringPaymentsTable } from './RecurringPaymentsTable';
import { RecurringPaymentsTableProvider } from './RecurringPaymentsTableProvider';
import { generateExports } from 'api/contacts/exports';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import { getComputedContactProps } from 'assets/js/pages/contacts/transform';
import * as CancelRecurringPlanAlertDialog from 'assets/js/pages/recurring-payments/CancelRecurringPlanAlertDialog';
import { RecurringPaymentsMetrics } from 'assets/js/pages/recurring-payments/RecurringPaymentsMetrics';
import { RecurringPaymentsSplash } from 'assets/js/pages/recurring-payments/RecurringPaymentsSplash';
import { RecurringPlanStatusBadge } from 'assets/js/pages/recurring-payments/RecurringPlanStatusBadge';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/recurring-payments/utils';
import { TransactionMethodPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import * as DropdownMenu from 'components/dropdown-menu';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Import from 'components/imports/Import';
import * as SubNav from 'components/sub-nav';
import { TransactionsSubNavTabs } from 'components/sub-nav/TransactionsSubNavTabs';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { PolymorphicTransaction } from 'types/domain';

const RecurringPayments = () => {
    const toast = useToast();
    const { newExportModal } = useFlags();
    const { filters } = useFiltersByKey('plans');
    const history = useHistory();
    const { accountId, isRecurringPaymentsEnabled } = useCurrentAccount();

    const { mutateAsync: exportPlans } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'account',
                report_type: 'csv',
                resource: 'recurring_payments',
                object_id: accountId,
                filters,
            }),
        onError: () => {
            toast.error('Unable to generate report');
        },
    });

    if (!isRecurringPaymentsEnabled) {
        return <RecurringPaymentsSplash />;
    }

    return (
        <FilterProvider cacheKey={'plans'} resource={'plans'}>
            <div className={'flex flex-col gap-y-4'}>
                <SubNav.Root>
                    <SubNav.Header>
                        <SubNav.Title>Recurring plans</SubNav.Title>
                        <SubNav.Actions>
                            {newExportModal ? (
                                <ExportDialog.Root reportType="recurring_payments">
                                    <ExportDialog.Trigger asChild={true}>
                                        <Button variant="outline" color="neutral">
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export
                                        </Button>
                                    </ExportDialog.Trigger>
                                </ExportDialog.Root>
                            ) : (
                                <ExportDataDialog.Root
                                    label="Recurring Plans"
                                    onSubmit={exportPlans}
                                >
                                    <ExportDataDialog.Trigger asChild={true}>
                                        <Button variant="outline" color="neutral">
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export
                                        </Button>
                                    </ExportDataDialog.Trigger>
                                </ExportDataDialog.Root>
                            )}

                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faFileImport} />
                                        Import
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item
                                                disabled={true}
                                                className="grid grid-cols-[16px_1fr] items-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faFileImport} />
                                                Recurring plans import
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <Import.Root strategy="transaction_plans">
                                            <Import.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onClick={e => e.preventDefault()}
                                                    className="grid grid-cols-[16px_1fr] items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faFileImport} />
                                                    Recurring plans import
                                                </DropdownMenu.Item>
                                            </Import.Trigger>
                                            <Import.Portal>
                                                <Import.Space />
                                            </Import.Portal>
                                        </Import.Root>
                                    </Suspense>
                                    <DropdownMenu.Item
                                        onClick={() =>
                                            history.push(`/accounts/${accountId}/settings/imports`)
                                        }
                                    >
                                        View import history
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </SubNav.Actions>
                    </SubNav.Header>
                    <TransactionsSubNavTabs />
                </SubNav.Root>
                <RecurringPaymentsMetrics />
                <RecurringPaymentsTable />
            </div>
        </FilterProvider>
    );
};

const WrappedRecurringPayments = () => {
    const currencySymbol = useCurrencySymbol();
    return (
        <TableCoreResourceProvider
            resource={'transaction_plans'}
            initialState={{
                sortingState: [{ id: 'next_bill_date', desc: true }],
                columnVisibilityState: {
                    cancel_reason: false,
                    canceled_at: false,
                    method: false,
                    start_at: false,
                    email: false,
                    phone: false,
                    total_contributions: false,
                    fund: false,
                    payment_method: false,
                },
                columnOrderState: [
                    'contact_name',
                    'campaign',
                    'next_bill_date',
                    'amount',
                    'status',
                ],
            }}
            columns={columnHelper => [
                columnHelper.accessor('contact', {
                    id: 'contact_name',
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const plan = info.row.original;
                        const contact = info.getValue();

                        if (!plan) {
                            return <EmptyData />;
                        }

                        if (!contact) {
                            return (
                                <>
                                    {plan.first_name} {plan.last_name}
                                </>
                            );
                        }

                        const contactProps = getComputedContactProps(contact);

                        return (
                            <Link
                                className={'text-black hover:underline'}
                                to={contactProps.linkUrl}
                            >
                                <div className="flex gap-2">
                                    {contactProps.contactName}
                                    {contactProps.isCompany && (
                                        <Badge
                                            size="sm"
                                            className="flex gap-1 bg-blue-50 text-xs text-blue-500"
                                        >
                                            <FontAwesomeIcon icon={faBuilding} /> Company
                                        </Badge>
                                    )}
                                </div>
                            </Link>
                        );
                    },
                }),
                columnHelper.accessor('campaign', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => (
                        <div className={'flex max-w-[200px]'}>
                            <TruncatedText>{info.getValue().title}</TruncatedText>
                        </div>
                    ),
                }),
                columnHelper.accessor('next_bill_date', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        if (info.row.original.canceled_at) {
                            return <EmptyData />;
                        }

                        return (
                            <p>
                                {isoDateFromServer(info.getValue()).toLocaleString(
                                    DateTime.DATE_FULL,
                                )}
                            </p>
                        );
                    },
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('amount', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const plan = info.row.original;

                        return (
                            <div className={'flex items-center gap-2'}>
                                <p>
                                    {currencySymbol}
                                    {maskDecimal(info.getValue())}
                                </p>
                                <Badge>{capitalize(plan.frequency)}</Badge>
                            </div>
                        );
                    },
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('status', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => <RecurringPlanStatusBadge plan={info.row.original} />,
                }),
                columnHelper.accessor('email', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => <p>{info.getValue() ?? <EmptyData />}</p>,
                }),
                columnHelper.accessor('phone', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const phone = info.getValue();

                        return (
                            <p className="whitespace-nowrap">
                                {phone ? getFormattedPhoneNumber(phone) : <EmptyData />}
                            </p>
                        );
                    },
                }),
                columnHelper.accessor('total_contributions', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const totalContributions = info.getValue();

                        return (
                            <p>
                                {totalContributions ? (
                                    `${currencySymbol}${totalContributions}`
                                ) : (
                                    <EmptyData />
                                )}
                            </p>
                        );
                    },
                }),
                columnHelper.accessor('payment_method', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const paymentMethod = info.getValue();

                        if (paymentMethod) {
                            return (
                                <TransactionMethodPresentational
                                    transaction={
                                        info.row.original as unknown as PolymorphicTransaction
                                    }
                                />
                            );
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('canceled_at', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const rowValue = info.getValue();

                        if (!rowValue) {
                            return <EmptyData />;
                        }

                        return (
                            <p>{isoDateFromServer(rowValue).toLocaleString(DateTime.DATE_FULL)}</p>
                        );
                    },
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('cancel_reason', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => <p>{info.getValue()}</p>,
                }),
                columnHelper.accessor('start_at', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => (
                        <p>
                            {isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_FULL)}
                        </p>
                    ),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('fund', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const fund = info.getValue();

                        if (fund) {
                            return <p>{fund.name}</p>;
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.display({
                    id: Table.Constants.Actions,
                    cell: info => {
                        const plan = info.row.original;
                        const isCancelled = plan.status === 'cancelled';

                        return (
                            <DropdownMenu.Root key={plan.id}>
                                <Table.RowDropdownTrigger />
                                <DropdownMenu.Portal>
                                    <DropdownMenu.Content sideOffset={5} align={'end'}>
                                        <CancelRecurringPlanAlertDialog.Root plan={plan}>
                                            <CancelRecurringPlanAlertDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                    disabled={isCancelled}
                                                >
                                                    Cancel Plan
                                                </DropdownMenu.Item>
                                            </CancelRecurringPlanAlertDialog.Trigger>
                                        </CancelRecurringPlanAlertDialog.Root>
                                    </DropdownMenu.Content>
                                </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
            ]}
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: true,
                enableExpanding: true,
                meta: {
                    enableColumnOrder: true,
                },
            }}
        >
            <RecurringPaymentsTableProvider>
                <Content>
                    <RecurringPayments />
                </Content>
            </RecurringPaymentsTableProvider>
        </TableCoreResourceProvider>
    );
};

export default WrappedRecurringPayments;
